import React from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as IconCheckCircle } from '../../assets/images/icons/check_circle_filled.svg';
import { useLocale } from '../../contexts/LocaleContext';
import { centsToDollarsNumber } from '../../lib/util';
import * as types from '../../types';
import SimpleDismissableModal from '../modals/SimpleDismissableModal';
import { BOX_CONTENTS, PickYourFiDeviceKey } from './HardwareFirstBelowFold';
import styles from './HardwareFirstLearnMoreModal.module.scss';

// per the figma learn-more overlays: the "why <device>" bullet keys per device
const WHY_KEYS: Record<PickYourFiDeviceKey, string[]> = {
  mini: [
    'hardware_first_tag_mini',
    'hardware_first_why_attaches',
    'hardware_first_why_ip68',
    'hardware_first_why_same_gps',
  ],
  s3: [
    'hardware_first_tag_s3',
    'hardware_first_why_integrated_collar',
    'hardware_first_why_ip68_66k',
    'hardware_first_why_3_months_battery',
    'hardware_first_why_apple_watch',
  ],
  ultra: [
    'hardware_first_why_adventuring',
    'hardware_first_why_attaches',
    'hardware_first_why_ip68',
    'hardware_first_why_callback',
    'hardware_first_why_1_week_battery',
  ],
};

const DESCRIPTION_KEYS: Record<PickYourFiDeviceKey, string> = {
  mini: 'hardware_first_learn_more_description_mini',
  s3: 'hardware_first_learn_more_description_s3',
  ultra: 'hardware_first_learn_more_description_ultra',
};

interface SpecTableRow {
  labelKey: string;
  valueKey: string;
  subKey?: string;
}

// values mirror the figma learn-more spec sheets, corrected to the site's canonical
// figures where they disagree (mini battery, s3 weight - flagged in the pr)
const SPEC_TABLE: Record<PickYourFiDeviceKey, SpecTableRow[]> = {
  mini: [
    { labelKey: 'hardware_first_spec_label_battery', valueKey: 'hardware_first_spec_mini_battery' },
    { labelKey: 'hardware_first_spec_label_weight', valueKey: 'hardware_first_spec_mini_weight' },
    { labelKey: 'hardware_first_spec_label_attachment', valueKey: 'hardware_first_spec_value_universal_attach' },
    { labelKey: 'hardware_first_spec_label_charging', valueKey: 'hardware_first_spec_value_usb_c' },
    { labelKey: 'hardware_first_spec_label_waterproof', valueKey: 'hardware_first_spec_value_ip68' },
    { labelKey: 'hardware_first_spec_label_night_light', valueKey: 'hardware_first_spec_value_none' },
    { labelKey: 'hardware_first_spec_label_cellular', valueKey: 'hardware_first_spec_verizon' },
    { labelKey: 'hardware_first_spec_label_satellite', valueKey: 'hardware_first_spec_value_none' },
  ],
  s3: [
    {
      labelKey: 'hardware_first_spec_label_battery',
      valueKey: 'hardware_first_spec_s3_battery',
      subKey: 'hardware_first_spec_s3_battery_sub',
    },
    { labelKey: 'hardware_first_spec_label_weight', valueKey: 'hardware_first_spec_s3_weight' },
    { labelKey: 'hardware_first_spec_label_attachment', valueKey: 'hardware_first_spec_s3_attachment' },
    { labelKey: 'hardware_first_spec_label_charging', valueKey: 'hardware_first_spec_s3_charging' },
    { labelKey: 'hardware_first_spec_label_waterproof', valueKey: 'hardware_first_spec_value_ip68_66k' },
    { labelKey: 'hardware_first_spec_label_night_light', valueKey: 'hardware_first_spec_value_yes' },
    { labelKey: 'hardware_first_spec_label_cellular', valueKey: 'hardware_first_spec_s3_cellular' },
    { labelKey: 'hardware_first_spec_label_satellite', valueKey: 'hardware_first_spec_value_none' },
  ],
  ultra: [
    { labelKey: 'hardware_first_spec_label_battery', valueKey: 'hardware_first_spec_ultra_battery' },
    { labelKey: 'hardware_first_spec_label_attachment', valueKey: 'hardware_first_spec_value_universal_attach' },
    { labelKey: 'hardware_first_spec_label_charging', valueKey: 'hardware_first_spec_value_usb_c' },
    { labelKey: 'hardware_first_spec_label_waterproof', valueKey: 'hardware_first_spec_value_ip68_66k' },
    { labelKey: 'hardware_first_spec_label_satellite', valueKey: 'hardware_first_spec_ultra_satellite' },
  ],
};

interface HardwareFirstLearnMoreModalProps {
  deviceKey: PickYourFiDeviceKey;
  name: string;
  image: string;
  discount: types.HardwareDiscountPreview | undefined;
  trigger: JSX.Element;
  onOpen?: () => void;
  /** Called when the bottom CTA is tapped; the modal closes itself first. */
  onSelect: () => void;
}

export default function HardwareFirstLearnMoreModal({
  deviceKey,
  name,
  image,
  discount,
  trigger,
  onOpen,
  onSelect,
}: HardwareFirstLearnMoreModalProps) {
  const { t } = useTranslation();
  const { wholeNumberCurrencyFormatter } = useLocale();

  return (
    <SimpleDismissableModal trigger={trigger} onOpen={onOpen} containerClassName={styles.modal}>
      {(close) => (
        <div className={styles.container}>
          <div className={styles.heroWrap}>
            <img className={styles.hero} src={image} alt={name} />
          </div>
          <div className={styles.body}>
            <div className={styles.header}>
              <h2 className={styles.name}>{name}</h2>
              <div className={styles.description}>{t(DESCRIPTION_KEYS[deviceKey])}</div>
              {discount && (
                <div className={styles.priceBlock}>
                  <span>
                    <span className={styles.price}>
                      {wholeNumberCurrencyFormatter.format(centsToDollarsNumber(discount.discountedPriceInCents))}
                    </span>
                    {discount.listPriceInCents > discount.discountedPriceInCents && (
                      <span className={styles.listPrice}>
                        {wholeNumberCurrencyFormatter.format(centsToDollarsNumber(discount.listPriceInCents))}
                      </span>
                    )}
                  </span>
                  <span className={styles.planLine}>{t('hardware_first_plus_gps_plan')}</span>
                </div>
              )}
            </div>
            <div className={styles.section}>
              <div className={styles.eyebrow}>{t('hardware_first_why_device', { device: name })}</div>
              <div className={styles.whyList}>
                {WHY_KEYS[deviceKey].map((key) => (
                  <div key={key} className={styles.whyRow}>
                    <IconCheckCircle className={styles.whyCheck} />
                    <span>{t(key)}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className={styles.section}>
              <div className={styles.eyebrow}>{t('whats_in_the_box')}</div>
              <div className={styles.boxGrid}>
                {BOX_CONTENTS[deviceKey].map((item) => (
                  <div key={item.url} className={styles.boxItem}>
                    <img className={styles.boxImage} src={item.url} alt={t(item.labelKey)} />
                    <div className={styles.boxLabel}>{t(item.labelKey)}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className={styles.section}>
              <div className={styles.eyebrow}>{t('specs')}</div>
              <div className={styles.specTable}>
                {SPEC_TABLE[deviceKey].map((row) => (
                  <div key={row.labelKey} className={styles.specTableRow}>
                    <span className={styles.specLabel}>{t(row.labelKey)}</span>
                    <span className={styles.specValue}>
                      {t(row.valueKey)}
                      {row.subKey && <span className={styles.specValueSub}>{t(row.subKey)}</span>}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className={styles.ctaBar}>
            <button
              type="button"
              className={styles.cta}
              onClick={() => {
                close();
                onSelect();
              }}
            >
              {deviceKey === 's3'
                ? t('hardware_first_select_size_color')
                : t('hardware_first_select_device', { device: name })}
            </button>
          </div>
        </div>
      )}
    </SimpleDismissableModal>
  );
}
